import { useQuery } from "@tanstack/react-query";
import type {
  HyperliquidClearinghouseState,
  HyperliquidFundingEntry,
  HyperliquidMeta,
  HyperliquidSpotClearinghouseState,
} from "@/lib/hyperliquid/types";
import { HL_POLL_INTERVAL } from "@/lib/hyperliquid/constants";

const HL_PROXY = "/api/hyperliquid";
const FUNDING_LOOKBACK_MS = 7 * 24 * 60 * 60 * 1000;

export async function fetchHl<T>(body: Record<string, unknown>): Promise<T> {
  const res = await fetch(HL_PROXY, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error?: string }).error || "Hyperliquid API error");
  }
  return res.json() as Promise<T>;
}

// pollInterval is intentionally NOT part of the queryKey: surfaces that share
// an address share a cache entry, and TanStack picks the shortest active
// refetchInterval across observers. Trade-off: when a 30 s surface (gas
// badge) and a 5 s surface (wallet management) are mounted simultaneously,
// the 5 s poll wins for both — fewer HTTP calls, slightly more re-renders on
// the 30 s surface. If a future caller wants its own cadence isolated, key it
// in: ["hl-positions", address, pollInterval].
export function getHlPositionsQueryOptions(
  address: string | undefined,
  enabled = true,
  pollInterval: number = HL_POLL_INTERVAL,
) {
  return {
    queryKey: ["hl-positions", address] as const,
    queryFn: () => fetchHl<HyperliquidClearinghouseState>({ type: "clearinghouseState", user: address }),
    enabled: enabled && !!address,
    staleTime: pollInterval,
    refetchInterval: pollInterval,
  };
}

export function useHyperliquidPositions(
  address: string | undefined,
  enabled = true,
  pollInterval: number = HL_POLL_INTERVAL,
) {
  return useQuery(getHlPositionsQueryOptions(address, enabled, pollInterval));
}

export function getHlSpotBalanceQueryOptions(
  address: string | undefined,
  enabled = true,
  pollInterval: number = HL_POLL_INTERVAL,
) {
  return {
    queryKey: ["hl-spot", address] as const,
    queryFn: () => fetchHl<HyperliquidSpotClearinghouseState>({ type: "spotClearinghouseState", user: address }),
    enabled: enabled && !!address,
    staleTime: pollInterval,
    refetchInterval: pollInterval,
  };
}

export function useHyperliquidSpotBalance(
  address: string | undefined,
  enabled = true,
  pollInterval: number = HL_POLL_INTERVAL,
) {
  return useQuery(getHlSpotBalanceQueryOptions(address, enabled, pollInterval));
}

export type HyperliquidAbstraction = "unifiedAccount" | "portfolioMargin" | "disabled" | null;

export function useHyperliquidAbstraction(address: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["hl-abstraction", address],
    queryFn: () => fetchHl<HyperliquidAbstraction>({ type: "userAbstraction", user: address }),
    enabled: enabled && !!address,
    staleTime: 30_000,
  });
}

export function useHyperliquidFunding(address: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["hl-funding", address],
    queryFn: () =>
      fetchHl<HyperliquidFundingEntry[]>({
        type: "userFunding",
        user: address,
        startTime: Date.now() - FUNDING_LOOKBACK_MS,
      }),
    enabled: enabled && !!address,
    staleTime: 60_000,
  });
}

export function useHyperliquidMarkets(dex?: string, enabled = true) {
  return useQuery({
    queryKey: ["hl-markets", dex ?? "crypto"],
    queryFn: () => fetchHl<HyperliquidMeta>({ type: "meta", ...(dex ? { dex } : {}) }),
    enabled,
    staleTime: 60_000,
  });
}
